import {
  ApplicationRef,
  ComponentRef,
  Injectable,
  NgZone,
  ViewContainerRef,
} from "@angular/core";
import { IEvrideoDialogService } from "../../domain/common/i-evrideo-dialog-service";
import {
  EvrideoDialogComponent,
  EvrideoDialogOptions,
  EvrideoLoadingDialogOptions,
} from "../../components/common/evrideo-dialog/evrideo-dialog.component";

@Injectable()
export class EvrideoDialogService implements IEvrideoDialogService {
  private dialogRef: ComponentRef<EvrideoDialogComponent> | null = null;
  constructor(
    private _appRef: ApplicationRef,
    private _ngZone: NgZone,
  ) {}

  openDialog(
    viewContainerRef: ViewContainerRef,
    options: EvrideoDialogOptions,
  ): Promise<boolean> {
    this.dialogRef = viewContainerRef.createComponent(EvrideoDialogComponent);
    const instance = this.dialogRef.instance;
    instance.dialogType = options.type;
    instance.title = options.title;
    instance.hideIcon = options.hideIcon;
    instance.confirmButtonText = options.confirmButtonText;
    instance.cancelButtonText = options.cancelButtonText;
    instance.timeout = options.timeout;
    instance.backDropOpacity = options.backDropOpacity;
    instance.hideCloseButton = options.hideCloseButton;
    if (typeof options.content === "string") {
      instance.contentText = options.content;
    } else {
      instance.contentComponent = options.content;
      instance.contentContext = {
        ...(options.contentContext || {}),
        dialogConfirm: () => instance.confirm(),
        dialogCancel: () => instance.cancel(),
      };
    }

    const resultPromise = new Promise<boolean>((resolve) => {
      instance.confirmed.subscribe(() => {
        if (this.dialogRef) {
          this.dialogRef.destroy();
          this.dialogRef = null;
        }
        resolve(true);
      });

      instance.cancelled.subscribe(() => {
        if (this.dialogRef) {
          this.dialogRef.destroy();
          this.dialogRef = null;
        }
        resolve(false);
      });
    });

    setTimeout(() => {
      this._ngZone.run(() => {
        instance.open();
        this._appRef.tick();
      });
    }, 0);

    return resultPromise;
  }
  openDialogWithLoading<T>(
    viewContainerRef: ViewContainerRef,
    loadingOptions: EvrideoLoadingDialogOptions,
    delayOrPromise: number | Promise<T>,
    successOptions?: (result: T) => EvrideoDialogOptions,
    errorOptions?: (error: any) => EvrideoDialogOptions,
  ): Promise<{ result: boolean; reason: "confirm" | "cancel" | "escape" }> {
    const cancelLoadingPromise = new Promise<T>((_, reject) =>
      setTimeout(() => {
        reject(new Error("operation taking too long, aborting"));
      }, 31000),
    );

    this.dialogRef = viewContainerRef.createComponent(EvrideoDialogComponent);
    const instance = this.dialogRef.instance;
    instance.dialogType = loadingOptions.type || "simple";
    instance.title = loadingOptions.title;
    instance.timeout = -1;
    instance.hideCloseButton = true;
    if (typeof loadingOptions.loadingContent === "string") {
      instance.contentText = loadingOptions.loadingContent;
    } else {
      instance.contentComponent = loadingOptions.loadingContent;
      instance.contentContext = loadingOptions.loadingContentContext || {};
    }

    setTimeout(() => {
      instance.open();
    });
    // Handle the operation
    if (typeof delayOrPromise === "number") {
      setTimeout(() => {
        if (successOptions != undefined) {
          const options = successOptions(undefined);
          instance.updateContent(options);
        } else {
          instance.confirm();
        }
      }, delayOrPromise);
    } else {
      Promise.race<T>([delayOrPromise, cancelLoadingPromise])
        .then((result) => {
          if (successOptions != undefined) {
            const options = successOptions(result);
            instance.updateContent(options);
          } else {
            instance.confirm();
          }
        })
        .catch((error) => {
          if (errorOptions !== undefined) {
            const options = errorOptions(error);
            instance.updateContent(options);
          } else {
            instance.updateContent({
              title: loadingOptions.title || "Error",
              type: "error",
              content: "Something went wrong",
              confirmButtonText: "Ok",
            });
          }
        });

      // Return promise for dialog result
      return new Promise((resolve) => {
        instance.confirmed.subscribe(() => {
          setTimeout(() => {
            this.dialogRef?.destroy();
            this.dialogRef = null;
          }, 150);
          resolve({ result: true, reason: "confirm" });
        });

        instance.cancelled.subscribe(() => {
          setTimeout(() => {
            this.dialogRef?.destroy();
            this.dialogRef = null;
          }, 150);
          resolve({ result: false, reason: "cancel" });
        });
      });
    }
  }
}
